import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronRight, MoreHorizontal } from 'lucide-react'
import { Screen } from '../components/Screen'
import { Field, useLeaveGuard } from '../components/Form'
import { LimitNote, limitReason, type LimitReason } from '../components/LimitNote'
import { EmptyState, ErrorState, SaveFailedAlert, Sheet, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { useCoach, useCoachCards, useGroups, useInvalidateCoaches, useSingleFlight } from '../data/hooks'
import { SliceApiError, type CoachCard, type CoachPatch } from '../data/types'
import { useBackTo } from './AthleteScreen'

/** Без доступа в приложение: справочник не связан с участниками клуба. */
function DirectoryNote() {
  return (
    <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 13 }}>
      Тренер здесь - запись справочника направления: для расписания, групп и Штаба. Доступа в приложение он не получает -
      для этого приглашение в «Команде и доступах» (раздел «Клуб»).
    </p>
  )
}

function telHref(phone: string) {
  return `tel:${phone.replace(/[^\d+]/g, '')}`
}

/**
 * Назначить и снять группы тренера по разнице «было -> стало» тем же
 * вызовом, что форма группы (setGroupCoach). Повтор безопасен: сервер
 * назначенного второй раз не назначает. Возвращает число сбоев.
 */
async function applyGroupDiff(
  setGroupCoach: (groupId: string, coachId: string, assigned: boolean) => Promise<void>,
  coachId: string,
  before: string[],
  after: string[],
): Promise<number> {
  let failed = 0
  const ops: [string, boolean][] = [
    ...after.filter((id) => !before.includes(id)).map((id): [string, boolean] => [id, true]),
    ...before.filter((id) => !after.includes(id)).map((id): [string, boolean] => [id, false]),
  ]
  for (const [groupId, on] of ops) {
    try {
      await setGroupCoach(groupId, coachId, on)
    } catch {
      failed += 1
    }
  }
  return failed
}

// ---------------------------------------------------------------------------
// Справочник тренеров направления (/club/coaches)
// ---------------------------------------------------------------------------

export function CoachesScreen() {
  const navigate = useNavigate()
  const back = useBackTo('/club')
  const coaches = useCoachCards()

  let body
  if (coaches.isPending) body = <Skeleton rows={3} label="Загружаю тренеров" />
  else if (coaches.isError) body = <ErrorState title="Не удалось загрузить тренеров" onRetry={() => void coaches.refetch()} />
  else if (coaches.data.length === 0)
    body = <EmptyState title="Тренеров пока нет" text="Добавьте тренера, чтобы назначать его на группы." />
  else
    body = (
      <ul className="pc-list pc-card" style={{ padding: '0 12px', marginTop: 16 }}>
        {coaches.data.map((c) => (
          <li key={c.id}>
            <button type="button" className="pc-list-row" onClick={() => navigate(`/club/coaches/${c.id}`)}>
              <span className="pc-list-row__main">
                <span className="pc-list-row__title" style={{ display: 'block' }}>
                  {c.name}
                </span>
                <span className="pc-list-row__meta">
                  {c.groups.length > 0 ? c.groups.map((g) => g.name).join(', ') : 'Без групп'}
                  {c.phone ? ` / ${c.phone}` : ''}
                </span>
              </span>
              <ChevronRight size={18} aria-hidden="true" className="pc-muted" />
            </button>
          </li>
        ))}
      </ul>
    )

  return (
    <Screen
      title="Тренеры направления"
      back={back}
      actionBar={
        <button type="button" className="pc-btn pc-btn--primary pc-btn--block" onClick={() => navigate('/club/coaches/new')}>
          Добавить тренера
        </button>
      }
    >
      {body}
      <DirectoryNote />
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Группы тренера флажками
// ---------------------------------------------------------------------------

function GroupsPicker({ selected, onChange }: { selected: string[]; onChange: (ids: string[]) => void }) {
  const groups = useGroups()
  return (
    <section className="pc-section" aria-labelledby="pc-coach-groups">
      <h2 id="pc-coach-groups" className="pc-section-title">
        Ведёт группы
      </h2>
      {groups.isPending ? (
        <Skeleton rows={1} label="Загружаю группы" />
      ) : groups.isError ? (
        <ErrorState title="Не удалось загрузить группы" onRetry={() => void groups.refetch()} />
      ) : groups.data.length === 0 ? (
        <p className="pc-muted" style={{ margin: 0, fontSize: 14 }}>
          В направлении пока нет групп.
        </p>
      ) : (
        <ul className="pc-list pc-card pc-group-pick" aria-label="Группы направления">
          {groups.data.map((g) => (
            <li key={g.id}>
              <label className="pc-check-row">
                <input
                  type="checkbox"
                  checked={selected.includes(g.id)}
                  onChange={(e) => onChange(e.target.checked ? [...selected, g.id] : selected.filter((x) => x !== g.id))}
                />
                <span className="pc-list-row__title">{g.name}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function ContactFields({
  phone,
  email,
  onPhone,
  onEmail,
  initial,
}: {
  phone: string
  email: string
  onPhone: (v: string) => void
  onEmail: (v: string) => void
  initial?: { phone: string; email: string }
}) {
  return (
    <>
      <Field label="Телефон" hint={initial?.phone && !phone.trim() ? 'Телефон будет стёрт при сохранении.' : 'Можно не заполнять'}>
        {(p) => <input {...p} className="pc-input" type="tel" value={phone} maxLength={50} autoComplete="off" onChange={(e) => onPhone(e.target.value)} />}
      </Field>
      <Field label="Почта" hint={initial?.email && !email.trim() ? 'Почта будет стёрта при сохранении.' : 'Можно не заполнять'}>
        {(p) => <input {...p} className="pc-input" type="email" value={email} maxLength={200} autoComplete="off" onChange={(e) => onEmail(e.target.value)} />}
      </Field>
    </>
  )
}

// ---------------------------------------------------------------------------
// Новый тренер (/club/coaches/new)
// ---------------------------------------------------------------------------

export function CoachNewScreen() {
  const navigate = useNavigate()
  const { api, toast } = useEnv()
  const invalidate = useInvalidateCoaches()
  const flight = useSingleFlight()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [groupIds, setGroupIds] = useState<string[]>([])
  const [nameError, setNameError] = useState<string>()
  const [failure, setFailure] = useState<LimitReason | 'failed' | null>(null)
  const dirty = !!(name.trim() || phone.trim() || email.trim() || groupIds.length)
  const leave = useLeaveGuard(dirty)
  const back = () => leave.guard(() => navigate('/club/coaches', { replace: true }))

  const create = async () => {
    if (!name.trim()) {
      setNameError('Укажите имя тренера')
      return
    }
    setNameError(undefined)
    setFailure(null)
    try {
      // Всё до ухода с экрана - в одном полёте: второе нажатие не добавит второго
      await flight.run(async () => {
        const created = await api.createCoachCard({ name: name.trim(), phone, email })
        // Тренер уже есть: сбой назначения его не теряет
        const failed = await applyGroupDiff((g, c, on) => api.setGroupCoach(g, c, on), created.id, [], groupIds)
        await invalidate(created.id)
        toast(failed > 0 ? 'Тренер добавлен. Группы назначить не удалось: назначьте в правке тренера' : 'Тренер добавлен')
        navigate(`/club/coaches/${created.id}`, { replace: true })
      })
    } catch (e) {
      setFailure(limitReason(e))
    }
  }

  return (
    <Screen
      title="Новый тренер"
      back={back}
      hideHub
      hideNav
      actionBar={
        <>
          <button type="button" className="pc-btn" style={{ flex: 1 }} onClick={back}>
            Отмена
          </button>
          <button type="button" className="pc-btn pc-btn--primary" style={{ flex: 2 }} disabled={flight.busy} onClick={() => void create()}>
            {flight.busy ? 'Добавляю…' : 'Добавить'}
          </button>
        </>
      }
    >
      <form
        className="pc-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          void create()
        }}
      >
        {failure === 'failed' && <SaveFailedAlert>Тренер не добавлен, попробуйте ещё раз.</SaveFailedAlert>}
        {(failure === 'limit' || failure === 'locked') && <LimitNote what="тренеров" reason={failure} />}
        <Field label="Имя тренера" error={nameError}>
          {(p) => <input {...p} className="pc-input" value={name} maxLength={200} autoComplete="off" onChange={(e) => setName(e.target.value)} />}
        </Field>
        <ContactFields phone={phone} email={email} onPhone={setPhone} onEmail={setEmail} />
      </form>
      <GroupsPicker selected={groupIds} onChange={setGroupIds} />
      <DirectoryNote />
      {leave.dialog}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Карточка тренера (/club/coaches/:id)
// ---------------------------------------------------------------------------

export function CoachScreen() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const back = useBackTo('/club/coaches')
  const coach = useCoach(id)
  const [menu, setMenu] = useState(false)
  const [deleting, setDeleting] = useState(false)

  if (coach.isPending) {
    return (
      <Screen title="Тренер" back={back}>
        <Skeleton rows={3} />
      </Screen>
    )
  }
  if (coach.isError) {
    const gone = coach.error instanceof SliceApiError && coach.error.kind === 'not_found'
    return (
      <Screen title="Тренер" back={back}>
        {gone ? (
          <EmptyState title="Тренера нет в справочнике" text="Его удалили." />
        ) : (
          <ErrorState title="Не удалось загрузить тренера" onRetry={() => void coach.refetch()} />
        )}
      </Screen>
    )
  }

  const c = coach.data
  return (
    <Screen
      title={c.name}
      subtitle="Тренер направления"
      back={back}
      headerAction={
        <button type="button" className="pc-icon-btn" aria-label="Действия с тренером" onClick={() => setMenu(true)}>
          <MoreHorizontal size={20} aria-hidden="true" />
        </button>
      }
    >
      <section className="pc-card" style={{ marginTop: 16 }} aria-label="Контакт">
        <div className="pc-kv__label">Контакт</div>
        <div className="pc-kv__value" style={{ fontSize: 18 }}>
          {c.phone ? <a href={telHref(c.phone)}>{c.phone}</a> : 'Телефон не указан'}
        </div>
        {c.email && (
          <div className="pc-kv__value">
            <a href={`mailto:${c.email}`}>{c.email}</a>
          </div>
        )}
      </section>

      <section className="pc-section" aria-labelledby="pc-coach-card-groups">
        <h2 id="pc-coach-card-groups" className="pc-section-title">
          Ведёт группы
        </h2>
        {c.groups.length === 0 ? (
          <p className="pc-muted" style={{ margin: 0, fontSize: 14 }}>
            Пока ни одной. Назначить можно в правке тренера или группы.
          </p>
        ) : (
          <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
            {c.groups.map((g) => (
              <li key={g.id}>
                <button type="button" className="pc-list-row" onClick={() => navigate(`/groups/${g.id}`)}>
                  <span className="pc-list-row__main pc-list-row__title">{g.name}</span>
                  <ChevronRight size={18} aria-hidden="true" className="pc-muted" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <DirectoryNote />

      {menu && (
        <Sheet title={c.name} onClose={() => setMenu(false)}>
          <button
            type="button"
            className="pc-btn pc-btn--block"
            onClick={() => {
              setMenu(false)
              navigate(`/club/coaches/${c.id}/edit`)
            }}
          >
            Изменить
          </button>
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--danger"
            onClick={() => {
              setMenu(false)
              setDeleting(true)
            }}
          >
            Удалить тренера
          </button>
        </Sheet>
      )}
      {deleting && <DeleteCoachSheet coach={c} onClose={() => setDeleting(false)} />}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Правка тренера (/club/coaches/:id/edit)
// ---------------------------------------------------------------------------

export function CoachEditScreen() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const coach = useCoach(id)
  const back = () => navigate(`/club/coaches/${id}`, { replace: true })
  if (coach.isSuccess) return <CoachEditForm coach={coach.data} />
  return (
    <Screen title="Правка тренера" back={back} hideHub hideNav>
      {coach.isPending ? (
        <Skeleton rows={3} />
      ) : coach.error instanceof SliceApiError && coach.error.kind === 'not_found' ? (
        <EmptyState title="Тренера нет в справочнике" text="Его удалили." />
      ) : (
        <ErrorState title="Не удалось загрузить тренера" onRetry={() => void coach.refetch()} />
      )}
    </Screen>
  )
}

function CoachEditForm({ coach: c }: { coach: CoachCard }) {
  const navigate = useNavigate()
  const { api, toast } = useEnv()
  const invalidate = useInvalidateCoaches()
  const flight = useSingleFlight()
  const [initial] = useState(() => ({ name: c.name, phone: c.phone, email: c.email, groupIds: c.groups.map((g) => g.id) }))
  const [name, setName] = useState(initial.name)
  const [phone, setPhone] = useState(initial.phone)
  const [email, setEmail] = useState(initial.email)
  const [groupIds, setGroupIds] = useState(initial.groupIds)
  const [nameError, setNameError] = useState<string>()
  const [failed, setFailed] = useState<'failed' | 'gone' | null>(null)
  const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x))
  const dirty =
    name.trim() !== initial.name ||
    phone.trim() !== initial.phone.trim() ||
    email.trim() !== initial.email.trim() ||
    !sameSet(groupIds, initial.groupIds)
  const leave = useLeaveGuard(dirty)
  const toCard = () => navigate(`/club/coaches/${c.id}`, { replace: true })
  const back = () => leave.guard(toCard)

  const save = async () => {
    if (!name.trim()) {
      setNameError('Имя не может быть пустым')
      return
    }
    setNameError(undefined)
    setFailed(null)
    try {
      await flight.run(async () => {
        const patch: CoachPatch = {}
        if (name.trim() !== initial.name) patch.name = name.trim()
        if (phone.trim() !== initial.phone.trim()) patch.phone = phone.trim()
        if (email.trim() !== initial.email.trim()) patch.email = email.trim()
        if (Object.keys(patch).length > 0) await api.updateCoach(c.id, patch)
        const groupFailed = await applyGroupDiff((g, coachId, on) => api.setGroupCoach(g, coachId, on), c.id, initial.groupIds, groupIds)
        if (groupFailed > 0) throw new SliceApiError('group assignment failed', 'network')
        await invalidate(c.id)
        toast('Изменения сохранены')
        toCard()
      })
    } catch (e) {
      // Всё, что успело сохраниться, повтор отправит ещё раз без вреда.
      setFailed(e instanceof SliceApiError && e.kind === 'not_found' ? 'gone' : 'failed')
      void invalidate(c.id)
    }
  }

  return (
    <Screen
      title="Правка тренера"
      subtitle={c.name}
      back={back}
      hideHub
      hideNav
      actionBar={
        <>
          <button type="button" className="pc-btn" style={{ flex: 1 }} onClick={back}>
            Отмена
          </button>
          <button type="button" className="pc-btn pc-btn--primary" style={{ flex: 2 }} disabled={flight.busy || !dirty} onClick={() => void save()}>
            {flight.busy ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </>
      }
    >
      <form
        className="pc-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        {failed === 'failed' && <SaveFailedAlert>Изменения остались на экране, попробуйте ещё раз.</SaveFailedAlert>}
        {failed === 'gone' && <SaveFailedAlert>Тренера уже удалили из справочника: сохранить некуда.</SaveFailedAlert>}
        <Field label="Имя тренера" error={nameError}>
          {(p) => <input {...p} className="pc-input" value={name} maxLength={200} autoComplete="off" onChange={(e) => setName(e.target.value)} />}
        </Field>
        <ContactFields phone={phone} email={email} onPhone={setPhone} onEmail={setEmail} initial={initial} />
      </form>
      <GroupsPicker selected={groupIds} onChange={setGroupIds} />
      {leave.dialog}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Удаление тренера
// ---------------------------------------------------------------------------

/**
 * Что станет при удалении - по коду: тренер мягко удаляется из справочника,
 * из групп он пропадает сразу (назначения с удалённым тренером не
 * показываются), вход в приложение не меняется - справочник с участниками
 * клуба не связан.
 */
export function DeleteCoachSheet({ coach: c, onClose }: { coach: CoachCard; onClose: () => void }) {
  const navigate = useNavigate()
  const { api, toast } = useEnv()
  const client = useQueryClient()
  const invalidate = useInvalidateCoaches()
  const flight = useSingleFlight()
  const [failed, setFailed] = useState(false)

  const finish = (text: string) => {
    toast(text)
    navigate('/club/coaches', { replace: true })
    client.removeQueries({ queryKey: ['redesign', 'coach', c.id] })
    void invalidate()
  }

  const remove = async () => {
    setFailed(false)
    try {
      const ok = await flight.run(() => api.deleteCoach(c.id))
      if (ok === null) return
      finish(`Тренер удалён из справочника: ${c.name}`)
    } catch (e) {
      // Ответ на первое удаление потерялся, а тренер уже удалён - это успех.
      if (e instanceof SliceApiError && e.kind === 'not_found') finish(`Тренер уже удалён из справочника: ${c.name}`)
      else setFailed(true)
    }
  }

  return (
    <Sheet title={`Удалить тренера «${c.name}»?`} onClose={onClose}>
      <div className="pc-aa-note pc-aa-note--danger">
        <b>Пропадёт</b>
        <span>- запись из справочника направления;</span>
        <span>
          {c.groups.length > 0
            ? `- тренер из групп: ${c.groups.map((g) => g.name).join(', ')}.`
            : '- в группах тренер не назначен.'}
        </span>
      </div>
      <div className="pc-aa-note">
        <b>Не изменится</b>
        <span>- группы, их состав и расписание;</span>
        <span>- вход в приложение: справочник с участниками клуба не связан.</span>
      </div>
      <p className="pc-muted" style={{ margin: '12px 0 0', fontSize: 14 }}>
        Вернуть тренера из приложения нельзя - только добавить заново.
      </p>
      {failed && <SaveFailedAlert>Тренер не удалён, попробуйте ещё раз.</SaveFailedAlert>}
      <button
        type="button"
        className="pc-btn pc-btn--block pc-btn--danger"
        style={{ marginTop: 12 }}
        disabled={flight.busy}
        onClick={() => void remove()}
      >
        {flight.busy ? 'Удаляю…' : 'Удалить тренера'}
      </button>
      <button type="button" className="pc-btn pc-btn--block" onClick={onClose}>
        Отмена
      </button>
    </Sheet>
  )
}
